/**
 * The selection bar (ADR 0194, decision 5; the prototype's `selBarHTML` and
 * its `.sx-` styles): at the foot of the board while cards are selected, on a
 * desktop, with no popup open.
 *
 * - **"N selected"**, then what applies to them all (`bulkActions`), then
 *   **Clear**. An action that only some of the selection could take part in
 *   is greyed, with a short reason ("different clients").
 * - **Dismiss ▾** is the shared menu (`MenuButton`), clear of the bar's top:
 *   one reason for them all.
 * - **Merge into one** merges at once; the merged block's popup opens.
 * - **Mark as vendor…** and **Close tickets…** ask first, above the row: the
 *   senders and numbers to remember, or the tickets to close. Focus goes to
 *   its button; Cancel or Esc goes back to the action.
 * - **Esc** in the bar closes a confirm, then clears the selection.
 *
 * A labelled region; the count is said through the day view's status region
 * as it changes. A bulk action's failure stays here, as an alert, with the
 * cards it couldn't do still selected.
 *
 * `BoardNotice` sits above it: what a card's menu copied, the text to copy by
 * hand when the browser refused, or why a card's action failed.
 */
import { type KeyboardEvent, type Ref, useLayoutEffect, useRef } from "react";

import type { DismissReason } from "../../api/types";
import { CrossIcon } from "../../components/ui/Icons";
import { MenuButton } from "../../components/ui/Menu";
import { bulkDismissItems } from "./cardMenu";
import {
  type BulkAction,
  type BulkActionId,
  type PickableCard,
  type VendorParty,
  blocksToLog,
  count,
  ticketsToClose,
  vendorOf,
} from "./selection";
import { ticketName } from "./tickets";

/** The bar's confirms: Mark as vendor… and Close tickets…. */
export type BarConfirm = Extract<BulkActionId, "vendor" | "close">;

const button =
  "inline-flex h-[34px] cursor-pointer items-center gap-1.5 rounded-pill border px-3.5 text-[13px] font-semibold whitespace-nowrap " +
  "disabled:cursor-default disabled:border-dashed disabled:border-line disabled:text-muted ";
const plain =
  button +
  "border-line bg-panel text-text hover:enabled:border-brand hover:enabled:bg-panel-2 " +
  "aria-expanded:border-brand aria-expanded:bg-brand-tint aria-expanded:text-brand-ink-on-tint";
const warn =
  button +
  "border-status-needs-client-line bg-panel text-status-needs-client-fg " +
  "hover:enabled:border-status-needs-client-fg hover:enabled:bg-status-needs-client-bg " +
  "aria-expanded:border-status-needs-client-fg aria-expanded:bg-status-needs-client-bg";
const cta =
  "h-10 cursor-pointer rounded-pill bg-cta-bg px-5 text-[13.5px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50";
const secondary =
  "h-10 cursor-pointer rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2";
const panel =
  "rounded-[16px] border border-line bg-panel text-text " +
  "shadow-[0_2px_6px_rgba(0,0,0,0.06),0_16px_40px_rgba(0,0,0,0.16)] " +
  "dark:shadow-[0_2px_6px_rgba(0,0,0,0.4),0_16px_40px_rgba(0,0,0,0.55)]";

function VendorConfirm({
  parties,
  blocks,
  busy,
  goRef,
  onGo,
  onCancel,
}: {
  parties: readonly VendorParty[];
  blocks: number;
  busy: boolean;
  goRef: Ref<HTMLButtonElement>;
  onGo: () => void;
  onCancel: () => void;
}) {
  return (
    <div role="group" aria-label="Mark as vendor" className="flex max-w-[540px] flex-col gap-1.5 px-2 pt-1.5 pb-0.5">
      <p className="text-[14px] font-[650]">{`Remember ${parties.length === 1 ? "this" : "these"} as a vendor?`}</p>
      <ul className="flex flex-col gap-0.5 text-[13px]">
        {parties.map((party) => (
          <li key={`${party.kind}:${party.value}`}>
            <b>{party.shown}</b>
            <span className="text-muted">{` · never count its ${party.kind === "email" ? "email" : "calls"}`}</span>
          </li>
        ))}
      </ul>
      <p className="text-[12px] leading-[1.45] text-muted">
        {`For everyone. The ${count(blocks, "block is", "blocks are")} dismissed as a vendor's.`}
      </p>
      <div className="flex flex-wrap gap-2 pt-0.5">
        <button ref={goRef} type="button" disabled={busy} onClick={onGo} className={cta}>
          {blocks > 1 ? `Mark ${String(blocks)} as vendor` : "Mark as vendor"}
        </button>
        <button type="button" onClick={onCancel} className={secondary}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function CloseConfirm({
  picked,
  techName,
  busy,
  goRef,
  onGo,
  onCancel,
}: {
  picked: readonly PickableCard[];
  techName: string | null;
  busy: boolean;
  goRef: Ref<HTMLButtonElement>;
  onGo: () => void;
  onCancel: () => void;
}) {
  const tickets = ticketsToClose(picked);
  const n = tickets.length;
  return (
    <div role="group" aria-label="Close tickets" className="flex max-w-[540px] flex-col gap-1.5 px-2 pt-1.5 pb-0.5">
      <p className="text-[14px] font-[650]">{`Close ${count(n, "ticket", "tickets")}?`}</p>
      <ul className="flex flex-col gap-0.5 text-[13px]">
        {tickets.map(({ ticket, card }) => (
          <li key={ticket.ticketId}>
            <b className="font-mono text-[12.5px]">{ticketName(ticket)}</b>
            <span className="text-muted">
              {` · ${card.title} · ${ticket.statusLabel ?? "Open"} `}
              <span aria-hidden="true">→</span>
              <span className="sr-only">becomes</span>
              {" Complete"}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[12px] leading-[1.45] text-muted">
        {`Written as ${techName ?? "the tech who logged them"}. Axon Time reads each first: one that's already Complete is left as it is.`}
      </p>
      <div className="flex flex-wrap gap-2 pt-0.5">
        <button ref={goRef} type="button" disabled={busy} onClick={onGo} className={cta}>
          {`Close ${count(n, "ticket", "tickets")}`}
        </button>
        <button type="button" onClick={onCancel} className={secondary}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function SelectionBar({
  picked,
  actions,
  confirm,
  busy,
  error,
  techName,
  onDismiss,
  onMerge,
  onAsk,
  onConfirm,
  onCancel,
  onClear,
}: {
  /** The selected cards, in the board's order. */
  picked: readonly PickableCard[];
  /** What applies to them all (`bulkActions`). */
  actions: readonly BulkAction[];
  /** The confirm that's open, if any. */
  confirm: BarConfirm | null;
  /** What's under way ("Closing 2 tickets…"), while it is. */
  busy: string | null;
  /** Why the last bulk action didn't all go through. */
  error: string | null;
  /** Whose day it is: who a close is written as. */
  techName: string | null;
  onDismiss: (reason: DismissReason) => void;
  onMerge: () => void;
  onAsk: (confirm: BarConfirm) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onClear: () => void;
}) {
  const go = useRef<HTMLButtonElement>(null);
  const askButtons = useRef(new Map<BarConfirm, HTMLButtonElement>());
  const region = useRef<HTMLElement>(null);
  // A confirm opened: its button takes focus. Closed again (Cancel, Esc),
  // focus goes back to the action that asked, if it was in the bar.
  const asked = useRef<BarConfirm | null>(null);
  useLayoutEffect(() => {
    if (confirm !== null) {
      asked.current = confirm;
      go.current?.focus();
      return;
    }
    const was = asked.current;
    asked.current = null;
    const focused = document.activeElement;
    if (was !== null && (focused === null || focused === document.body || region.current?.contains(focused) === true)) {
      askButtons.current.get(was)?.focus();
    }
  }, [confirm]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape" || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    event.preventDefault();
    if (confirm !== null) {
      onCancel();
    } else {
      onClear();
    }
  };

  const disabled = busy !== null;
  const control = (action: BulkAction) => {
    const why = action.ok ? null : <span className="text-[12px] font-medium">{` · ${action.why ?? ""}`}</span>;
    if (action.id === "dismiss") {
      return action.ok ? (
        <MenuButton
          key="dismiss"
          menuLabel="Dismiss as"
          items={() => bulkDismissItems({ dismiss: onDismiss })}
          clearOf={(at) => at.closest("[data-part='selection-bar']")}
          disabled={disabled}
          className={warn}
        >
          Dismiss
          <span aria-hidden="true" className="text-[11px]">
            ▾
          </span>
        </MenuButton>
      ) : (
        <button key="dismiss" type="button" disabled className={warn}>
          Dismiss
          {why}
        </button>
      );
    }
    if (action.id === "merge") {
      return (
        <button key="merge" type="button" disabled={!action.ok || disabled} onClick={onMerge} className={plain}>
          {action.label}
          {why}
        </button>
      );
    }
    const id = action.id;
    return (
      <button
        key={id}
        ref={(element) => {
          if (element === null) {
            askButtons.current.delete(id);
          } else {
            askButtons.current.set(id, element);
          }
        }}
        type="button"
        disabled={!action.ok || disabled}
        aria-expanded={action.ok ? confirm === id : undefined}
        onClick={() => {
          if (confirm === id) {
            onCancel();
          } else {
            onAsk(id);
          }
        }}
        className={plain}
      >
        {action.label}
        {why}
      </button>
    );
  };

  return (
    <section
      ref={region}
      aria-label="Selected cards"
      data-part="selection-bar"
      onKeyDown={onKeyDown}
      className={`${panel} pointer-events-auto flex max-w-full flex-col gap-2 p-[7px] motion-safe:animate-block-in`}
    >
      {confirm === "vendor" ? (
        <VendorConfirm
          parties={vendorOf(blocksToLog(picked))}
          blocks={blocksToLog(picked).length}
          busy={disabled}
          goRef={go}
          onGo={onConfirm}
          onCancel={onCancel}
        />
      ) : confirm === "close" ? (
        <CloseConfirm picked={picked} techName={techName} busy={disabled} goRef={go} onGo={onConfirm} onCancel={onCancel} />
      ) : null}
      {busy === null ? null : <p className="px-2 pt-1 text-[13.5px] font-[650]">{busy}</p>}
      {error === null ? null : (
        <p role="alert" className="max-w-[540px] px-2 pt-1 text-[13px] text-status-needs-client-fg">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="pr-2 pl-[7px] text-[13.5px] whitespace-nowrap">
          <b className="font-bold tabular-nums">{picked.length}</b> selected
        </span>
        {actions.map(control)}
        <button
          type="button"
          aria-label="Clear the selection"
          title="Clear the selection (Esc)"
          onClick={onClear}
          className={`${plain} pr-3 pl-[9px] text-muted`}
        >
          <CrossIcon />
          <span>Clear</span>
        </button>
      </div>
    </section>
  );
}

/** What a card's menu has to say at the foot of the board. */
export type Notice =
  | { kind: "copied"; text: string }
  /** The browser refused: the text, selected, to copy by hand. */
  | { kind: "blocked"; text: string }
  | { kind: "failed"; text: string }
  /** Just dismissed, with Undo for a while (Discussion #877): the blocks. */
  | { kind: "dismissed"; text: string; blockIds: readonly string[] };

export function BoardNotice({
  notice,
  onClose,
  onUndo,
}: {
  notice: Notice;
  onClose: () => void;
  /** Restores what a "dismissed" notice names. */
  onUndo?: (() => void) | undefined;
}) {
  const field = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (notice.kind === "blocked") {
      field.current?.focus();
      field.current?.select();
    }
  }, [notice]);
  const close = (
    <button
      type="button"
      onClick={onClose}
      className="h-8 shrink-0 cursor-pointer rounded-pill border border-line bg-panel px-3 text-[12.5px] font-semibold hover:bg-panel-2"
    >
      {notice.kind === "blocked" ? "Done" : "Close"}
    </button>
  );
  if (notice.kind === "copied") {
    return (
      <p data-part="board-notice" className={`${panel} pointer-events-auto max-w-[540px] truncate px-4 py-2 text-[13px]`}>
        {`Copied: ${notice.text}`}
      </p>
    );
  }
  if (notice.kind === "dismissed") {
    return (
      <div data-part="board-notice" className={`${panel} pointer-events-auto flex max-w-[540px] items-center gap-3 px-4 py-2`}>
        <p className="min-w-0 flex-1 truncate text-[13px]">{notice.text}</p>
        <button
          type="button"
          onClick={onUndo}
          className="h-8 shrink-0 cursor-pointer rounded-pill border border-line bg-panel px-3 text-[12.5px] font-semibold hover:bg-panel-2"
        >
          Undo
        </button>
      </div>
    );
  }
  if (notice.kind === "failed") {
    return (
      <div data-part="board-notice" className={`${panel} pointer-events-auto flex max-w-[540px] items-center gap-3 px-4 py-2`}>
        <p role="alert" className="min-w-0 flex-1 text-[13px] text-status-needs-client-fg">
          {notice.text}
        </p>
        {close}
      </div>
    );
  }
  return (
    <div
      role="alert"
      data-part="board-notice"
      className={`${panel} pointer-events-auto flex w-[min(460px,100%)] flex-col gap-1.5 px-4 py-3 text-[12.5px]`}
    >
      <label htmlFor="board-notice-copy" className="text-status-needs-client-fg">
        Copying was blocked here. Select it to copy it by hand:
      </label>
      <div className="flex items-center gap-2">
        <input
          ref={field}
          id="board-notice-copy"
          readOnly
          value={notice.text}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onClose();
            }
          }}
          className="h-9 min-w-0 flex-1 rounded-input border border-line bg-card px-3 font-mono text-[12px] text-text"
        />
        {close}
      </div>
    </div>
  );
}
